import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./styles.module.scss";
import PagePanel from "app/components/PagePanel";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { RootState } from "typedefs";
import SpaceModel from "app/models/SpaceModel";
import { useActions } from "app/utils/hooks";
import {
  fetchSegmentById,
  editSegment,
  clearSegments,
} from "app/actions/segments";
import { fetchSpaceMemberships } from "app/actions/space";
import Button from "app/components/Button/ButtonVariant";
import SegmentsLayout from "app/components/Layouts/SegmentsLayout";
import { useHistory } from "react-router-dom";
import { Loading } from "app/components/Wrappers";
import { cls } from "app/utils";
import SearchBar from "app/components/inputs/SearchBar";
import useDebounce from "app/utils/hooks/useDebounce";
import { usePagination } from "app/utils/hooks/usePagination";

type queryType = {
  forcePerPage?: number;
  perPage?: number;
  options?: {
    include?: string[];
    exclude?: string[];
  };
  type?: "athlete";
  athlete_name?: string;
  segment_id?: string;
  smart_list_id?: string;
};

const FORCE_PER_PAGE = 100;
const PER_PAGE = FORCE_PER_PAGE;

const OPTIONS = {
  "options[include]": ["segment_names"],
};

const DEFAULT_SPACE_QUERY: queryType = {
  ...OPTIONS,
  type: "athlete",
  forcePerPage: FORCE_PER_PAGE,
  perPage: PER_PAGE,
};

const ManageSpaces = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const id = window.location.pathname.split("/")[2];

  const segmentActions = useActions({
    fetchSpaceMemberships,
    fetchSegmentById,
    clearSegments,
    editSegment,
  });

  const history = useHistory();

  const route = (path: string) => {
    history.push(path);
  };

  const [selectedSpaceIds, setSelectedSpaceIds] = useState<string[]>([]);
  const [selectAllSpaces, setSelectAllSpaces] = useState<boolean>(false);
  const [searchTerm, setSearch] = useState<string>("");

  const debouncedSearch = useDebounce(searchTerm, 500);

  const updateSearch = (value: string) => {
    if (value != null && value.length > 0) {
      setSearch(value);
    } else {
      setSearch("");
    }
  };

  const {
    data: spacesData,
    pending,
    sentryRef,
    loadInitialData,
  }: any = usePagination({
    initialParams: { ...DEFAULT_SPACE_QUERY, athlete_name: debouncedSearch },
    fetchData: (params, signal) =>
      segmentActions.fetchSpaceMemberships(params, signal),
    clearDataAction: () => {},
    dependencies: [debouncedSearch],
    fetchOnMount: false,
  });

  useEffect(() => {
    if (id) {
      segmentActions.fetchSegmentById(id);
      loadInitialData();
    }

    return () => {
      segmentActions.clearSegments();
      setSelectedSpaceIds(null);
    };
  }, [dispatch, id]);

  const { segmentsData, segmentsPending } = useSelector(
    (state: RootState) => state.segments,
    shallowEqual,
  );

  useEffect(() => {
    if (segmentsData) {
      setSelectedSpaceIds((segmentsData && segmentsData.spaceIds) || []);
    }
  }, [segmentsData]);

  useEffect(() => {
    if (selectAllSpaces && spacesData?.length) {
      const allSpaceIds = spacesData.map(
        (data: { space: SpaceModel }) => data?.space?.id,
      );
      setSelectedSpaceIds(allSpaceIds);
    }
  }, [selectAllSpaces, spacesData]);

  const cancelUrl = id ? `/lists/${id}/spaces` : "/lists/";

  const updateHandler = () => {
    const updatedSegmentData = selectAllSpaces
      ? { ...segmentsData, selectAllSpaces: true }
      : {
          ...segmentsData,
          spaceIds: selectedSpaceIds?.length !== 0 ? selectedSpaceIds : [null],
        };
    segmentActions.editSegment(id, updatedSegmentData).then(() => {
      setSelectedSpaceIds([]);
      setSelectAllSpaces(false);
      route(cancelUrl);
    });
  };

  const checkHandler = (spaceId: string) => {
    if (!spaceId) {
      return;
    }

    if (selectAllSpaces && selectedSpaceIds?.includes(spaceId)) {
      setSelectAllSpaces(false);
    }

    setSelectedSpaceIds((prevSelectedSpaceIds = []) =>
      prevSelectedSpaceIds.includes(spaceId)
        ? prevSelectedSpaceIds.filter((id) => id !== spaceId)
        : [...prevSelectedSpaceIds, spaceId],
    );
  };

  const selectAllHandler = () => {
    setSelectAllSpaces(true);
    const allSpaceIds = spacesData?.map(
      (data: { space: SpaceModel }) => data?.space?.id,
    );
    setSelectedSpaceIds(allSpaceIds);
  };

  const onCancel = () => {
    return history.push(cancelUrl);
  };

  return (
    <SegmentsLayout
      isLoading={segmentsPending}
      loaderChild={<h3 className="well_nothing">{t("Loading...")}</h3>}
      displayNoResult={!segmentsData}
      noResultChild={<h3>{t("No Spaces to select")}</h3>}
    >
      <div className={styles.container}>
        <PagePanel
          titleStyles={styles.page_title}
          title={t("Select {{name}}'s Spaces", {
            name: segmentsData && segmentsData.name,
          })}
        />

        <div className={styles.search_container}>
          <SearchBar
            placeholder={"Search for a space..."}
            className={styles.searchbar}
            onChange={updateSearch}
          />
        </div>

        <div className={styles.table_wrapper}>
          {spacesData && (
            <table className="segment_spaces table">
              <tbody>
                <tr>
                  <th className="segment-title">
                    <span>{t("Space")}</span>
                  </th>

                  <th className="segment-title">
                    {t("Already in other lists")}
                  </th>
                </tr>
                {spacesData?.map((data: { space: SpaceModel }) => {
                  return (
                    <tr key={data?.space?.id}>
                      <td>
                        <label className="checkbox-list">
                          <input
                            name={`space_${data?.space?.id}`}
                            onChange={() => checkHandler(data?.space?.id)}
                            checked={
                              selectedSpaceIds &&
                              selectedSpaceIds.includes(data?.space?.id)
                            }
                            type="checkbox"
                          />
                          {data?.space?.athleteName} &nbsp; {data?.space?.sport}
                        </label>
                      </td>

                      <td className="other-segments-label">
                        {data?.space?.segmentNames &&
                          data?.space?.segmentNames
                            ?.filter(
                              (segmentName: string[]) =>
                                segmentName !== segmentsData &&
                                segmentsData?.name,
                            )
                            ?.join(", ")}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {pending && <Loading isLoading={pending} loadType="spinner" />}
          <div ref={sentryRef} />
          <div
            className={cls(styles.buttons_wrapper, {
              ["mt-1"]: pending,
            })}
          >
            <Button
              title={t("Select All Spaces")}
              buttonType="primary"
              onClickFunc={selectAllHandler}
              disabled={pending || spacesData?.length === 0 || selectAllSpaces}
            />
            <Button
              title={t("Deselect All")}
              buttonType="secondary-outline"
              onClickFunc={() => {
                setSelectAllSpaces(false);
                setSelectedSpaceIds([]);
              }}
              disabled={!selectedSpaceIds?.length}
            />
            <Button
              title={t("Update")}
              buttonType="primary"
              onClickFunc={updateHandler}
              disabled={pending}
            />
            <Button
              title={t("Cancel")}
              buttonType="secondary-outline"
              onClickFunc={onCancel}
            />
          </div>
        </div>
      </div>
    </SegmentsLayout>
  );
};

export default ManageSpaces;
